import React, { memo, useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeDashboardDialog } from '@actions/dashboard-actions';

import BatchContext from '@components/batch/batch-upload-edit/batch-context';

import * as dialog from '@constants/dialogs';

import { getBatchRows } from '@selectors/batch-edit-selector';

import Dialog from '@shared/dialogs/dialog';
import DialogActions from '@shared/dialogs/dialog-actions';

import './upload-batch-dialog.scss';

const UploadBatchDialog = () => {
  const dispatch = useDispatch();
  const { activeDialogs, dialogParams } = useSelector(state => state.dashboard);
  const { batch, fileId } = useContext(BatchContext);
  const batchRows = useSelector(state => getBatchRows(state, fileId));

  const skippedCount = useMemo(
    () => Object.values(batchRows).filter(row => row.skipped).length,
    [batchRows]
  );

  const isDialogActive = useMemo(
    () => activeDialogs[dialog.UPLOAD_BATCH],
    [activeDialogs]
  );

  const { onUpload } = useMemo(
    () => dialogParams[dialog.UPLOAD_BATCH] || {},
    [dialogParams]
  );

  const { created, updated, total } = useMemo(() => {
    const rows = Object.values(batchRows || {});
    const updatedCount = rows.filter(row => row.data.id && !row.skipped).length;
    const createdCount = rows.length - updatedCount - skippedCount;

    return {
      created: createdCount,
      updated: updatedCount,
      total: createdCount + updatedCount
    };
  }, [batchRows, skippedCount]);

  const onClose = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.UPLOAD_BATCH));
  }, [dispatch]);

  const handleUpload = useCallback(() => {
    if (onUpload) {
      onUpload();
    }
    onClose();
  }, [onUpload, onClose]);

  if (!isDialogActive) {
    return null;
  }

  return (
    <Dialog
      actions={
        <DialogActions
          onCancel={onClose}
          onSubmit={handleUpload}
          submitLabel="UPLOAD"
        />
      }
      title={`Upload ${total} ${batch.entity}s?`}
    >
      <div styleName="upload-batch-dialog-content">
        <b>{created}</b> will be created.
        <b>{updated}</b> will be updated.
        {skippedCount > 0 && <span><b>{skippedCount}</b> will be skipped.</span>}
      </div>
    </Dialog>
  );
};

export default memo(UploadBatchDialog);
